import { Link } from "@corpuscomplete/web/components/link";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@corpuscomplete/web-ui/components/empty";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "@corpuscomplete/web-ui/components/context-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@corpuscomplete/web-ui/components/table";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "@corpuscomplete/web-ui/components/tabs";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { ListChecksIcon } from "lucide-react";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { ListCollectionDetail } from "@corpuscomplete/core-ui/common/list-collection/detail";

/**
 * List collection detail route boundary.
 * Contract: resolves collection identity from route params and delegates
 * list data ownership to ListCollectionDetail provider lifecycle.
 */
export function ListCollectionDetailPage({ id }: { id: string }) {
  const { userId } = useAuth();

  return (
    <ListCollectionDetail.DataProvider collectionId={id} userId={userId}>
      <ListCollectionDetailContent />
    </ListCollectionDetail.DataProvider>
  );
}

function ListCollectionDetailContent() {
  const collection = ListCollectionDetail.useCollection();
  const { isLoading } = ListCollectionDetail.useLoadingState();

  if (isLoading) {
    return null;
  }

  if (!collection) {
    return (
      <Page>
        <PageBody>
          <PageHeader>
            <Text variant="sm">Collection</Text>
          </PageHeader>
          <PageContent>
            <Empty className="flex-1">
              <EmptyHeader>
                <EmptyTitle>Collection not found</EmptyTitle>
                <EmptyDescription>
                  This collection may have been deleted or you don't have access.
                </EmptyDescription>
              </EmptyHeader>
              <Link to="/personal/lists">
                <Button variant="outline">Go to Lists</Button>
              </Link>
            </Empty>
          </PageContent>
        </PageBody>
      </Page>
    );
  }

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <div className="mr-auto flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Text variant="sm">{collection.name}</Text>
              {collection.isDefault && <Badge variant="secondary">Default</Badge>}
            </div>
          </div>
        </PageHeader>
        <PageContent>
          <Tabs defaultValue="lists">
            <TabsList>
              <TabsTrigger value="lists">Lists</TabsTrigger>
            </TabsList>
            <TabsPanel value="lists">
              <ListsTab />
            </TabsPanel>
          </Tabs>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function ListsTab() {
  const lists = ListCollectionDetail.useLists();
  const { isLoading } = ListCollectionDetail.useLoadingState();

  if (isLoading && lists.length === 0) {
    return null;
  }

  if (lists.length === 0) {
    return (
      <Empty className="flex-1 py-12">
        <EmptyHeader>
          <EmptyMedia>
            <div className="flex size-16 items-center justify-center rounded-full bg-muted">
              <ListChecksIcon className="size-8 text-muted-foreground" />
            </div>
          </EmptyMedia>
          <EmptyTitle>No lists in this collection</EmptyTitle>
          <EmptyDescription>Create lists from the Lists page to add them here.</EmptyDescription>
        </EmptyHeader>
        <Link to="/personal/lists">
          <Button variant="outline">Go to Lists</Button>
        </Link>
      </Empty>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Type</TableHead>
          <TableHead className="text-right">Items</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lists.map((list) => (
          <ContextMenu key={list.id}>
            <ContextMenuTrigger>
              <TableRow>
                <TableCell className="font-medium">
                  <Link
                    to="/personal/lists/$id"
                    params={{ id: list.id }}
                    className="hover:underline"
                  >
                    {list.title}
                  </Link>
                </TableCell>
                <TableCell>
                  <Text variant="sm" className="text-muted-foreground capitalize">
                    {list.type}
                  </Text>
                </TableCell>
                <TableCell className="text-right">
                  <Text variant="sm">{list.items.length}</Text>
                </TableCell>
              </TableRow>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <Link to="/personal/lists/$id" params={{ id: list.id }}>
                <ContextMenuItem>
                  <Text variant="sm">View</Text>
                </ContextMenuItem>
              </Link>
            </ContextMenuContent>
          </ContextMenu>
        ))}
      </TableBody>
    </Table>
  );
}
